import { useEffect, useRef, useState } from "react";
import { Avatar } from "@progress/kendo-react-layout";

import { getUserName } from "authProvider";
import { AnnouncementList } from "components/Announcement/AnnouncementList";
import { AppBar } from "components/AppBar/AppBar";
import { AppBarSection } from "components/AppBar/AppBarSection";
import { Button, ButtonMode } from "components/Button/Button";
import { Popup } from "components/Popup/Popup";
import TruLink from "components/TruLink/TruLink";

import { ActionsMenuList } from "../Menus/ActionsMenuList";
import MainMenuList from "../Menus/MainMenuList";
import UserMenu from "../Menus/UserMenu";
import UpgradeAnnouncements from "../UpgradeAnnouncements";

import "./TopNavigation.scss";

import type { To } from "react-router";
import type { EncounterState, UserState } from "models";
import type { AnnouncementsState } from "models/announcements";

const ACTIVE_MENU_POPUP = {
  help: "help",
  none: "none",
  user: "user",
} as const;

type ActiveMenuPopup = (typeof ACTIVE_MENU_POPUP)[keyof typeof ACTIVE_MENU_POPUP];

interface TopNavigationProps {
  announcements: AnnouncementsState;
  encounter: EncounterState;
  encounterOpened: boolean;
  errorCount?: number;
  hasChangesCallback?: (to: To, clickHandler?: () => void) => void;
  isErrorMode: boolean;
  isModal?: boolean;
  isStandalone?: boolean;
  onChangePasswordClick: () => void;
  onDismissAnnouncement: (id: string) => void;
  onLogoutClick: () => void;
  onShowErrorsClick: () => void;
  recoveredEncountersCount: number;
  refreshRecoveredEncountersCount: () => void;
  user: UserState;
  skipInit?: boolean;
}

const TopNavigation = ({
  announcements,
  encounter,
  encounterOpened,
  errorCount,
  hasChangesCallback,
  isErrorMode,
  isModal,
  isStandalone,
  onChangePasswordClick,
  onDismissAnnouncement,
  onLogoutClick,
  onShowErrorsClick,
  recoveredEncountersCount,
  refreshRecoveredEncountersCount,
  user,
  skipInit,
}: TopNavigationProps) => {
  const [activeMenuPopup, setActiveMenuPopup] = useState<ActiveMenuPopup>(ACTIVE_MENU_POPUP.none);
  const [showAnnouncement, setShowAnnouncement] = useState(false);

  const announcementRef = useRef<HTMLDivElement>(null);
  const userMenuAnchorRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);

  const canOnlySystemSettings = user.isItAdmin && !user.isSystemAdmin;

  useEffect(() => {
    if (!skipInit && !isStandalone && !canOnlySystemSettings) {
      refreshRecoveredEncountersCount();
    }
  }, [canOnlySystemSettings, isStandalone, refreshRecoveredEncountersCount, skipInit]);

  useEffect(() => {
    if (
      announcementRef.current &&
      announcements?.systemAnnouncements?.length > 0 &&
      !showAnnouncement
    ) {
      setShowAnnouncement(true);
    }
  }, [announcements, showAnnouncement]);

  const onPopupOpen = () => {
    if (menuRef.current) {
      menuRef.current.focus();
    }
  };

  const handleUserClick = () => {
    setActiveMenuPopup(ACTIVE_MENU_POPUP.user);
  };

  const closeMenuPopup = () => {
    setActiveMenuPopup(ACTIVE_MENU_POPUP.none);
  };

  const name = getUserName() as string;
  const userName = name ? name.split(" ") : "";
  const avatarTextFromB2C =
    userName ?
      `${userName[0].slice(0, 1).toUpperCase()}${userName[1].slice(0, 1).toUpperCase()}`
    : " ";

  return (
    <AppBar className="tc-appbar tc-appbar-v25" positionMode="static">
      <UpgradeAnnouncements
        announcements={announcements.upgradeAnnouncements}
        onDismissAnnouncement={onDismissAnnouncement}
      />
      <div className="appbar-container">
        <AppBarSection>
          {!isModal && !isStandalone && (
            <TruLink
              encounterOpened={encounter.inProgress.IsInterfaced && encounterOpened}
              hasChangesCallback={hasChangesCallback}
              isErrorMode={isErrorMode}
              to="/"
            >
              <div id="logo-container" className="logo-container">
                TruBridge Encoder
              </div>
            </TruLink>
          )}
          {(isModal ?? isStandalone) && (
            <div id="logo-container" className="logo-container">
              TruBridge Encoder
            </div>
          )}
        </AppBarSection>
        {!isStandalone && !isModal && !canOnlySystemSettings && (
          <AppBarSection>
            <MainMenuList
              encounterOpened={encounterOpened}
              hasChangesCallback={hasChangesCallback}
              isErrorMode={isErrorMode}
              user={user}
            />
          </AppBarSection>
        )}
        {user.organizationName && (
          <AppBarSection className="organization-name">
            <div>Org: {user.organizationName}</div>
          </AppBarSection>
        )}
        <AppBarSection className="actions">
          <ActionsMenuList
            alerts={{ count: errorCount, onClick: onShowErrorsClick }}
            encounterOpened={encounterOpened}
            hasChangesCallback={hasChangesCallback}
            isErrorMode={isErrorMode}
            recoveredEncountersCount={recoveredEncountersCount}
            showFullList={!isModal && !isStandalone}
            user={user}
          />
          <div ref={announcementRef} />
          <Popup
            anchor={announcementRef.current}
            anchorAlign={{ horizontal: "right", vertical: "bottom" }}
            contentKey={announcements.updatedTimestamp}
            popupAlign={{ horizontal: "right", vertical: "top" }}
            popupClass={`popup-content popup-${ACTIVE_MENU_POPUP.help}`}
            show={showAnnouncement && announcements.systemAnnouncements.length > 0}
          >
            <AnnouncementList
              content={announcements.systemAnnouncements}
              onDismissAnnouncement={onDismissAnnouncement}
            />
          </Popup>
          {window.useAzureADAuth && !isStandalone && !isModal && (
            <div ref={userMenuAnchorRef}>
              <Button mode={ButtonMode.Transparent} onClick={handleUserClick} showMenu>
                <Avatar themeColor="tertiary" type="text">
                  {user.avatarText || avatarTextFromB2C}
                </Avatar>
              </Button>
            </div>
          )}
          <Popup
            anchor={userMenuAnchorRef.current}
            anchorAlign={{ horizontal: "right", vertical: "bottom" }}
            onOpen={onPopupOpen}
            popupAlign={{ horizontal: "right", vertical: "top" }}
            popupClass={`popup-content popup-${ACTIVE_MENU_POPUP.user}`}
            show={activeMenuPopup === ACTIVE_MENU_POPUP.user}
          >
            <UserMenu
              changePassword={onChangePasswordClick}
              closeMenuPopup={closeMenuPopup}
              encounterOpened={encounterOpened}
              hasChangesCallback={hasChangesCallback}
              isErrorMode={isErrorMode}
              logout={onLogoutClick}
              ref={menuRef}
              user={user}
            />
          </Popup>
        </AppBarSection>
      </div>
    </AppBar>
  );
};

export default TopNavigation;
